/**
 * 租户管理 - 类型定义
 */

/**
 * 租户启用应用
 */
export type TenantEnabledApp = 'mock' | 'aic'

/**
 * 租户登录方式
 */
export type TenantLoginMethod = 'qr' | 'sms' | 'password' | 'google'

/**
 * 租户主页模式
 */
export type TenantHomepageMode = 'default' | 'url' | 'html'

/**
 * 租户 Logo 变体
 */
export type TenantLogoVariant = 'light' | 'dark'

/**
 * 在线客服提供商
 */
export type TenantSupportProvider = 'none' | 'wechat'

/**
 * 租户域名绑定
 */
export interface TenantHost {
  /** 域名绑定 ID */
  id: number
  /** 域名 */
  host: string
  /** 创建时间 */
  createdAt: string
}

/**
 * 管理端租户配置
 */
export interface AdminTenantConfig {
  /** 租户 ID */
  id: number
  /** 站点名称 */
  name: string
  /** 品牌名称 */
  brandName: string
  /** 品牌别名 */
  brandNameAlias: string
  /** 浅色 Logo 地址 */
  logoLightUrl: string
  /** 深色 Logo 地址 */
  logoDarkUrl: string
  /** Favicon 地址 */
  faviconUrl: string
  /** 在线客服提供商 */
  supportProvider: TenantSupportProvider
  /** 客服显示名称 */
  supportDisplayName: string
  /** 是否已配置微信对话平台 Token */
  supportWechatConfigured: boolean
  /** 微信对话平台 Token（仅详情接口返回） */
  supportWechatToken?: string
  /** 平台授予应用列表 */
  grantedApps: TenantEnabledApp[]
  /** 启用应用列表 */
  enabledApps: TenantEnabledApp[]
  /** 平台授予登录方式列表 */
  grantedLoginMethods: TenantLoginMethod[]
  /** 启用登录方式列表 */
  loginMethods: TenantLoginMethod[]
  /** 是否启用 */
  isActive: boolean
  /** 是否默认租户 */
  isDefault: boolean
  /** 是否为演示租户 */
  isDemo: boolean
  /** 主页模式 */
  homepageMode: TenantHomepageMode
  /** 主页内容 */
  homepageContent: string
  /** ICP备案号 */
  footerIcpNumber: string
  /** 公安备案号 */
  footerPsbNumber: string
  /** 创建时间 */
  createdAt: string
  /** 更新时间 */
  updatedAt: string
  /** 绑定域名列表 */
  hosts: TenantHost[]
}

/**
 * 租户列表查询参数
 */
export interface TenantListParams {
  /** 页码 */
  page?: number
  /** 每页数量 */
  limit?: number
}

/**
 * 租户列表响应
 */
export interface TenantListResponse {
  /** 租户列表 */
  tenants: AdminTenantConfig[]
  /** 总数 */
  total: number
  /** 当前页 */
  page: number
  /** 每页数量 */
  limit: number
}

/**
 * 创建租户请求
 */
export interface CreateTenantRequest {
  /** 站点名称 */
  name: string
  /** 品牌名称 */
  brandName: string
  /** 品牌别名 */
  brandNameAlias: string
  /** 子域名后缀（可选，不含 .magly.cn） */
  subdomain?: string
  /** 自定义域名列表（可选） */
  customDomains?: string[]
  /** 浅色 Logo 地址 */
  logoLightUrl: string
  /** 深色 Logo 地址 */
  logoDarkUrl: string
  /** 在线客服提供商 */
  supportProvider: TenantSupportProvider
  /** 客服显示名称 */
  supportDisplayName: string
  /** 微信对话平台 Token */
  supportWechatToken?: string
  /** 平台授予应用列表 */
  grantedApps: TenantEnabledApp[]
  /** 启用应用列表 */
  enabledApps: TenantEnabledApp[]
  /** 平台授予登录方式列表 */
  grantedLoginMethods: TenantLoginMethod[]
  /** 登录方式列表 */
  loginMethods: TenantLoginMethod[]
  /** 是否启用 */
  isActive: boolean
  /** 是否默认租户 */
  isDefault: boolean
  /** 是否为演示租户 */
  isDemo: boolean
  /** 主页模式 */
  homepageMode: TenantHomepageMode
  /** 主页内容 */
  homepageContent: string
  /** ICP备案号 */
  footerIcpNumber: string
  /** 公安备案号 */
  footerPsbNumber: string
}

/**
 * 更新租户请求
 */
export interface UpdateTenantRequest {
  /** 站点名称 */
  name?: string
  /** 品牌名称 */
  brandName?: string
  /** 品牌别名 */
  brandNameAlias?: string
  /** 自定义域名列表 */
  customDomains?: string[]
  /** 浅色 Logo 地址 */
  logoLightUrl?: string
  /** 深色 Logo 地址 */
  logoDarkUrl?: string
  /** 在线客服提供商 */
  supportProvider?: TenantSupportProvider
  /** 客服显示名称 */
  supportDisplayName?: string
  /** 微信对话平台 Token */
  supportWechatToken?: string
  /** 平台授予应用列表 */
  grantedApps?: TenantEnabledApp[]
  /** 启用应用列表 */
  enabledApps?: TenantEnabledApp[]
  /** 平台授予登录方式列表 */
  grantedLoginMethods?: TenantLoginMethod[]
  /** 登录方式列表 */
  loginMethods?: TenantLoginMethod[]
  /** 是否启用 */
  isActive?: boolean
  /** 是否默认租户 */
  isDefault?: boolean
  /** 是否为演示租户 */
  isDemo?: boolean
  /** 主页模式 */
  homepageMode?: TenantHomepageMode
  /** 主页内容 */
  homepageContent?: string
  /** ICP备案号 */
  footerIcpNumber?: string
  /** 公安备案号 */
  footerPsbNumber?: string
}

/**
 * 更新子域名请求
 */
export interface UpdateSubdomainRequest {
  /** 新的子域名后缀（传 null 表示移除子域名） */
  subdomain?: string | null
}

/**
 * 租户分页信息
 */
export interface TenantPagination {
  /** 当前页码 */
  page: number
  /** 每页数量 */
  limit: number
  /** 总数 */
  total: number
}

/**
 * 租户启用应用选项
 */
export const TENANT_ENABLED_APP_OPTIONS: Array<{ label: string; value: TenantEnabledApp }> = [
  { label: 'Mock', value: 'mock' },
  { label: 'AI 批改', value: 'aic' },
]

/**
 * 租户登录方式选项
 */
export const TENANT_LOGIN_METHOD_OPTIONS: Array<{ label: string; value: TenantLoginMethod }> = [
  { label: '扫码登录', value: 'qr' },
  { label: '短信验证码', value: 'sms' },
  { label: '密码登录', value: 'password' },
  { label: 'Google 登录', value: 'google' },
]

/**
 * 应用名称映射
 */
export const TENANT_ENABLED_APP_LABELS: Record<TenantEnabledApp, string> = {
  mock: 'Mock',
  aic: 'AI 批改',
}

/**
 * 登录方式名称映射
 */
export const TENANT_LOGIN_METHOD_LABELS: Record<TenantLoginMethod, string> = {
  qr: '扫码登录',
  sms: '短信验证码',
  password: '密码登录',
  google: 'Google 登录',
}

/**
 * 主页模式选项
 */
export const TENANT_HOMEPAGE_MODE_OPTIONS: Array<{ label: string; value: TenantHomepageMode; description: string }> = [
  { label: '默认主页', value: 'default', description: '使用平台当前默认首页' },
  { label: '外部 URL', value: 'url', description: '以 iframe 形式嵌入 https 页面' },
  { label: 'HTML 内容', value: 'html', description: '以内联 iframe 的 srcdoc 渲染 HTML/CSS' },
]

/**
 * 主页模式名称映射
 */
export const TENANT_HOMEPAGE_MODE_LABELS: Record<TenantHomepageMode, string> = {
  default: '默认主页',
  url: '外部 URL',
  html: 'HTML 内容',
}

/**
 * Logo 变体名称映射
 */
export const TENANT_LOGO_VARIANT_LABELS: Record<TenantLogoVariant, string> = {
  light: '浅色 Logo',
  dark: '深色 Logo',
}

/**
 * 租户 Logo 上传响应
 */
export interface TenantLogoUploadResponse {
  variant: TenantLogoVariant
  logoUrl: string
  previewUrl: string
}

/**
 * 租户 Favicon 上传响应
 */
export interface TenantFaviconUploadResponse {
  faviconUrl: string
  previewUrl: string
}

/**
 * 管理端在线客服预览请求
 */
export interface AdminTenantSupportPreviewRequest {
  tenantId?: number
  isDefault?: boolean
  tenantName: string
  supportProvider: TenantSupportProvider
  supportDisplayName: string
  supportWechatToken?: string
}

/**
 * 管理端在线客服预览响应
 */
export interface AdminTenantSupportPreviewResponse {
  provider: TenantSupportProvider
  url: string
}

export type TenantSettlementSourceType = 'MEMBERSHIP' | 'TUTOR_LESSON'
export type TenantSettlementRecordKind = 'REVENUE' | 'REFUND_ADJUSTMENT'
export type TenantSettlementStatus = 'PENDING' | 'SETTLED' | 'VOIDED'

/**
 * 租户月结记录。
 */
export interface TenantSettlementRecord {
  id: number
  sourceType: TenantSettlementSourceType
  sourceId: number
  sourceOrderNo: string
  recordKind: TenantSettlementRecordKind
  userId: number
  tutorId: number | null
  grossAmount: number
  platformAmount: number
  tenantAmount: number
  status: TenantSettlementStatus
  paidAt: string
  createdAt: string
}

/**
 * 租户月结总览项。
 */
export interface TenantSettlementOverviewItem {
  tenantId: number
  pendingCount: number
  pendingAmount: number
  pendingGrossAmount: number
  pendingPlatformAmount: number
}

/**
 * 租户月结总览响应。
 */
export interface TenantSettlementOverviewResponse {
  settlementMonth: string
  tenants: TenantSettlementOverviewItem[]
}

/**
 * 租户月结预览请求。
 */
export interface TenantSettlementPreviewRequest {
  tenantId: number
  settlementMonth: string
}

/**
 * 租户月结预览响应。
 */
export interface TenantSettlementPreviewResponse {
  tenantId: number
  settlementMonth: string
  recordCount: number
  grossAmount: number
  platformAmount: number
  tenantAmount: number
  canSettle: boolean
  allowedSettlementMonth: string
  settlementHint: string | null
  records: TenantSettlementRecord[]
}

/**
 * 租户月结确认请求。
 */
export interface ConfirmTenantSettlementRequest {
  tenantId: number
  settlementMonth: string
  paymentReference?: string
  note?: string
}

/**
 * 租户月结批次。
 */
export interface TenantSettlementBatch {
  id: number
  tenantId: number
  settlementMonth: string
  recordCount: number
  grossAmount: number
  platformAmount: number
  tenantAmount: number
  confirmedBy: number
  confirmedAt: string
  paymentReference: string | null
  note: string | null
  createdAt: string
}

/**
 * 租户月结确认响应。
 */
export interface ConfirmTenantSettlementResponse {
  batch: TenantSettlementBatch
}

/**
 * 租户月结批次列表响应。
 */
export interface TenantSettlementBatchListResponse {
  batches: TenantSettlementBatch[]
  total: number
}

/**
 * 租户月结批次详情响应。
 */
export interface TenantSettlementBatchDetailResponse {
  batch: TenantSettlementBatch
  records: TenantSettlementRecord[]
}
